import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ProximityTriggerNodePreview from "../../features/sequence/node_previews/ProximityTriggerNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import Interaction, { InteractionType } from "./Interaction";
import { ProximityTriggerMethod } from "./ProximityTrigger.shared";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export type ProximityTriggerColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export interface IProximityTrigger extends Interaction {
  posId: string;
  npcId: string;
  distance: number;
  method: ProximityTriggerMethod;
  visualize: boolean;
  lineWeight: number;
  colorOutline: ProximityTriggerColor;
  colorFill: ProximityTriggerColor;
}

export function getDefaultProximityTrigger(): IProximityTrigger {
  return {
    id: GetNewGuid(),
    name: "Proximity Trigger",
    type: InteractionType.ProximityTrigger,
    posId: "",
    npcId: "",
    distance: 1,
    method: ProximityTriggerMethod.OnEnter,
    visualize: false,
    lineWeight: 1,
    colorOutline: { r: 58, g: 196, b: 255, a: 1 },
    colorFill: { r: 58, g: 196, b: 255, a: 0.2 },
  };
}

export const proximityTriggerInteraction = (): InteractionFactoryEntity => {
  const create = (): IProximityTrigger => {
    return getDefaultProximityTrigger();
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IProximityTrigger => {
    const defaults = getDefaultProximityTrigger();
    const casted = interaction as Partial<IProximityTrigger>;

    return {
      ...defaults,
      ...casted,
      id: casted.id ?? defaults.id,
      name: casted.name ?? defaults.name,
      type: InteractionType.ProximityTrigger,
      distance:
        Number.isFinite(casted.distance) && casted.distance !== undefined
          ? casted.distance
          : defaults.distance,
      lineWeight:
        Number.isFinite(casted.lineWeight) && casted.lineWeight !== undefined
          ? casted.lineWeight
          : defaults.lineWeight,
      method:
        casted.method === ProximityTriggerMethod.OnEnter ||
        casted.method === ProximityTriggerMethod.OnExit
          ? casted.method
          : defaults.method,
      visualize: casted.visualize ?? defaults.visualize,
      colorOutline: casted.colorOutline ?? defaults.colorOutline,
      colorFill: casted.colorFill ?? defaults.colorFill,
    };
  };

  const copy = (interaction: Interaction): IProximityTrigger => {
    const casted = interaction as IProximityTrigger;
    return {
      ...casted,
      id: GetNewGuid(),
      name: casted.name ?? "Proximity Trigger",
      type: InteractionType.ProximityTrigger,
    };
  };

  const validate = (): string[] => {
    return [];
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const casted = interaction as IProximityTrigger;
    return getMatchingResults(
      compactSearchableStrings(
        interaction.name,
        casted.posId,
        searchLookupContext?.getActorSearchTerms(casted.npcId) ?? casted.npcId,
        casted.method,
      ),
      takeName,
      interaction,
      "Proximity Trigger",
    );
  };

  const validateErrors = (interaction: Interaction): string[] => {
    const casted = interaction as IProximityTrigger;
    const errors: string[] = [];

    if (!casted.posId?.trim()) {
      errors.push("Position is required");
    }
    if (!casted.npcId?.trim()) {
      errors.push("Actor is required");
    }
    if (!Number.isFinite(casted.distance) || casted.distance <= 0) {
      errors.push("Distance must be greater than 0");
    }
    if (!Number.isFinite(casted.lineWeight) || casted.lineWeight <= 0) {
      errors.push("Line Weight must be greater than 0");
    }
    if (
      casted.method !== ProximityTriggerMethod.OnEnter &&
      casted.method !== ProximityTriggerMethod.OnExit
    ) {
      errors.push("Method is required");
    }

    return errors;
  };

  return {
    View: <></>,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <ProximityTriggerNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: "Proximity Trigger",
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
